import { InPlaceEditorProps, Labeled, SelectField, SelectInput, Validator } from "react-admin";
import { AegleInPlaceEditor } from "./AegleInPlaceEditor";
import { US_STATES_AND_TERRITORIES } from "../inputs/UsStateInput";

/**
 * In-place editor for U.S. state selection fields.
 *
 * Displays the selected state using {@link SelectField} and allows editing via a {@link SelectInput}.
 * Uses a predefined list of U.S. states from {@link US_STATES}.
 * Wrapped in a {@link Labeled} component to optionally show a field label.
 *
 * @param source - The field name to bind to
 * @param label - Optional label displayed above the input
 * @param readOnly - If true, disables editing and shows display only
 * @param rest - Additional props passed to {@link InPlaceEditor} and {@link SelectInput}
 */
const InPlaceStateEditor = ({
  source,
  label,
  readOnly = false,
  validate,
  ...rest
}: {
  source: string;
  label?: string;
  readOnly?: boolean;
  validate?: Validator | Validator[];
} & Omit<InPlaceEditorProps, "input" | "source">) => {
  // Read-only mode: just show the display component
  if (readOnly) {
    return (
      <Labeled label={label}>
        <SelectField
          source={source}
          variant="body1"
          choices={US_STATES_AND_TERRITORIES}
          sx={{ display: "block", mb: 0.5 }}
          emptyText="-"
        />
      </Labeled>
    );
  }

  return (
    <Labeled label={label}>
      <AegleInPlaceEditor
        source={source}
        editor={
          <SelectInput
            {...rest}
            source={source}
            choices={US_STATES_AND_TERRITORIES}
            variant="standard"
            size="small"
            margin="none"
            label={false}
            helperText={validate ? undefined : false}
            validate={validate}
            autoFocus
            SelectProps={{ defaultOpen: true }}
            sx={{
              "& .MuiInput-root": { marginTop: 0 },
            }}
          />
        }
        {...rest}
      >
        <SelectField
          source={source}
          variant="body1"
          choices={US_STATES_AND_TERRITORIES}
          sx={{ display: "block", mb: 0.5 }}
          emptyText="-"
        />
      </AegleInPlaceEditor>
    </Labeled>
  );
};

export default InPlaceStateEditor;
